// 🎧 웨어러블 전용 마이그레이션 스크립트
// [배경] 웨어러블 데이터만 별도로 안전하게 마이그레이션
// [실행] 브라우저 콘솔에서:
//   - window.__previewWearableMigration(): 미리보기
//   - window.__migrateWearables(): 실제 마이그레이션
//   - window.__scanWearableData(): 현재 웨어러블 데이터 스캔
// 
// ⚠️ 웨어러블 ID 규칙 (스마트폰/태블릿과 다름):
// - 웨어러블은 "세대" 번호가 모델을 구분하는 핵심 정보
// - 예: airpods-1, airpods-2, airpods-pro-2 → 세대 번호 유지!
// - extractPureModelId()를 사용하면 안 됨

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  generateModelId,
  generatePriceId,
  generateAliases,
  DeviceCatalogDocument,
  UnifiedPriceDocument
} from '../types/deviceCatalog';

// 🔑 웨어러블 전용 ID 검증 (스마트폰/태블릿과 다른 규칙)
// - 웨어러블은 세대 번호(-1, -2, -pro-2 등)가 정상적인 ID의 일부
// - storage(-128gb-), connectivity(-wifi-), 긴 순번(-139 등)만 비정상
const isCanonicalWearableModelId = (modelId: string): boolean => {
  // 웨어러블 ID가 아니면 false
  if (!modelId.includes('wearable')) return false;
  
  // storage 패턴 포함 시 비정상 (예: -128gb-, -256gb-)
  if (/-\d+(gb|tb)-/i.test(modelId)) return false;
  
  // connectivity 패턴 포함 시 비정상 (예: -wifi-, -cellular-)
  if (/-wifi-|-cellular-/i.test(modelId)) return false;
  
  // 3자리 이상 순번은 비정상 (예: -139, -256)
  // 단, 세대 번호(1~4자리)는 정상
  const trailingNumber = modelId.match(/-(\d+)$/);
  if (trailingNumber && trailingNumber[1].length >= 3) return false;
  
  // 중복된 시리즈명 패턴 (예: airpods-pro-airpods-pro-1)
  const parts = modelId.split('-');
  for (let i = 0; i < parts.length - 2; i++) {
    if (parts[i] === parts[i + 2] && parts[i].length > 2) {
      return false; // 중복 발견
    }
  }
  
  return true;
};

// 🔑 웨어러블 전용 ID 정제 (세대 번호는 유지)
const cleanWearableModelId = (modelId: string): { cleanId: string; issues: string[] } => {
  const issues: string[] = [];
  let cleanId = modelId;
  
  // 1. storage 패턴 제거 (웨어러블에는 storage가 없어야 함)
  const storageMatch = cleanId.match(/-(\d+)(gb|tb)(-|$)/i);
  if (storageMatch) {
    issues.push('storage 포함');
    cleanId = cleanId.replace(new RegExp(`-${storageMatch[1]}${storageMatch[2]}`, 'i'), '');
  }
  
  // 2. connectivity 패턴 제거
  if (/-wifi-|-wifi$|-cellular-|-cellular$/i.test(cleanId)) {
    issues.push('connectivity 포함');
    cleanId = cleanId.replace(/-(wifi|cellular)(-|$)/gi, '$2');
  }
  
  // 3. 3자리 이상 순번 제거 (세대 번호는 유지)
  const trailingNumber = cleanId.match(/-(\d{3,})$/);
  if (trailingNumber) {
    issues.push(`순번(${trailingNumber[1]}) 포함`);
    cleanId = cleanId.replace(/-\d{3,}$/, '');
  }
  
  // 4. 중복된 시리즈명 정리 
  // 예: apple-wearable-airpods-pro-airpods-pro-1 → apple-wearable-airpods-pro-1
  // 패턴: prefix-xxx-yyy-xxx-yyy-N → prefix-xxx-yyy-N (접두사 보존)
  
  // 접두사 분리 (apple-wearable-, samsung-wearable- 등)
  const prefixMatch = cleanId.match(/^(apple|samsung)-wearable-/);
  const prefix = prefixMatch ? prefixMatch[0] : '';
  const withoutPrefix = prefix ? cleanId.substring(prefix.length) : cleanId;
  
  // 접두사 제외 부분에서 중복 패턴 찾기
  // 패턴: xxx-yyy-xxx-yyy-N → xxx-yyy-N
  const duplicateMatch = withoutPrefix.match(/^(.+?)-(.+?)-\1-\2(-\d+)?$/);
  if (duplicateMatch) {
    issues.push('중복 시리즈명');
    cleanId = `${prefix}${duplicateMatch[1]}-${duplicateMatch[2]}${duplicateMatch[3] || ''}`;
  }
  
  // 5. 연속 하이픈 정리
  cleanId = cleanId.replace(/-+/g, '-').replace(/-$/, '');
  
  return { cleanId, issues };
};

// 🔑 웨어러블 마이그레이션 결과
interface WearableMigrationResult {
  success: boolean;
  catalog: {
    scanned: number;
    canonical: number;
    nonCanonical: number;
    migrated: number;
    errors: string[];
  };
  prices: {
    scanned: number;
    canonical: number;
    nonCanonical: number;
    migrated: number;
    errors: string[];
  };
}

// 🔑 웨어러블 데이터 스캔 결과
interface WearableScanResult {
  catalog: {
    total: number;
    items: Array<{
      id: string;
      modelKo: string;
      brand: string;
      isCanonical: boolean;
      suggestedId?: string;
      issues?: string[];
    }>;
  };
  prices: {
    total: number;
    items: Array<{
      id: string;
      modelId: string;
      brand: string;
      storage?: string;
      isCanonical: boolean;
      gradePrices?: { A: number; B: number; C: number; D: number; E: number };
    }>;
  };
}

/**
 * 🔍 웨어러블 데이터 스캔 (현재 상태 확인)
 * ⚠️ 스마트폰/태블릿 데이터는 제외
 */
export async function scanWearableData(): Promise<WearableScanResult> {
  console.log('🎧 웨어러블 데이터 스캔 시작...');
  console.log('============================================');
  
  const result: WearableScanResult = {
    catalog: { total: 0, items: [] },
    prices: { total: 0, items: [] }
  };
  
  try {
    // 1. device_catalog에서 웨어러블 조회
    console.log('\n📦 device_catalog 웨어러블 스캔...');
    const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
    
    catalogSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const idLower = docSnap.id.toLowerCase();
      
      // ⛔ 스마트폰/태블릿 제외 (가장 먼저 체크)
      if (idLower.startsWith('smartphone-') || 
          idLower.startsWith('tablet-') ||
          data.deviceType === 'smartphone' ||
          data.deviceType === 'tablet' ||
          idLower.includes('iphone') ||
          idLower.includes('galaxy-s') ||
          idLower.includes('galaxy-note') ||
          idLower.includes('galaxy-flip') ||
          idLower.includes('galaxy-fold') ||
          idLower.includes('ipad')) {
        return; // 스킵
      }
      
      // 웨어러블 필터링 (deviceType 또는 카테고리로 판단)
      const isWearable = data.deviceType === 'wearable' || 
                         data.category === '웨어러블' ||
                         idLower.includes('wearable') ||
                         idLower.includes('airpods') ||
                         idLower.includes('buds') ||
                         idLower.includes('watch');
      
      if (isWearable) {
        const isCanonical = isCanonicalWearableModelId(docSnap.id);
        const { cleanId, issues } = cleanWearableModelId(docSnap.id);
        
        result.catalog.items.push({
          id: docSnap.id,
          modelKo: data.modelKo || '',
          brand: data.brand || '',
          isCanonical,
          suggestedId: isCanonical ? undefined : cleanId,
          issues: issues.length > 0 ? issues : undefined
        });
      }
    });
    
    result.catalog.total = result.catalog.items.length;
    
    // 2. unified_prices에서 웨어러블 조회
    console.log('\n💰 unified_prices 웨어러블 스캔...');
    const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
    
    pricesSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const idLower = docSnap.id.toLowerCase();
      const modelIdLower = (data.modelId || '').toLowerCase();
      
      // ⛔ 스마트폰/태블릿 제외 (가장 먼저 체크)
      if (idLower.startsWith('smartphone-') || 
          idLower.startsWith('tablet-') ||
          modelIdLower.startsWith('smartphone-') ||
          modelIdLower.startsWith('tablet-') ||
          data.deviceType === 'smartphone' ||
          data.deviceType === 'tablet' ||
          idLower.includes('iphone') ||
          idLower.includes('galaxy-s-') ||
          idLower.includes('galaxy-note') ||
          idLower.includes('ipad')) {
        return; // 스킵
      }
      
      // 웨어러블 필터링
      const isWearable = data.deviceType === 'wearable' ||
                         idLower.includes('wearable') ||
                         idLower.includes('airpods') ||
                         idLower.includes('buds') ||
                         modelIdLower.includes('wearable') ||
                         modelIdLower.includes('airpods');
      
      if (isWearable) {
        const modelId = data.modelId || '';
        const isCanonical = isCanonicalWearableModelId(modelId);
        
        result.prices.items.push({
          id: docSnap.id,
          modelId,
          brand: data.brand || '',
          storage: data.variant?.storage,
          isCanonical,
          gradePrices: data.gradePrices
        });
      }
    });
    
    result.prices.total = result.prices.items.length;
    
    // 결과 출력
    console.log('\n============================================');
    console.log('📊 웨어러블 스캔 결과:');
    console.log(`\n📦 device_catalog: ${result.catalog.total}개`);
    
    const canonicalCatalog = result.catalog.items.filter(i => i.isCanonical);
    const nonCanonicalCatalog = result.catalog.items.filter(i => !i.isCanonical);
    console.log(`  ✅ 정상 ID: ${canonicalCatalog.length}개`);
    console.log(`  ⚠️ 비정상 ID: ${nonCanonicalCatalog.length}개`);
    
    if (result.catalog.items.length > 0) {
      console.log('\n  📋 목록:');
      result.catalog.items.forEach(item => {
        const status = item.isCanonical ? '✅' : '⚠️';
        console.log(`    ${status} ${item.brand} - ${item.modelKo || item.id}`);
        if (!item.isCanonical) {
          console.log(`       현재: ${item.id}`);
          console.log(`       제안: ${item.suggestedId}`);
          if (item.issues) console.log(`       문제: ${item.issues.join(', ')}`);
        }
      });
    }
    
    console.log(`\n💰 unified_prices: ${result.prices.total}개`);
    
    const canonicalPrices = result.prices.items.filter(i => i.isCanonical);
    const nonCanonicalPrices = result.prices.items.filter(i => !i.isCanonical);
    console.log(`  ✅ 정상 modelId: ${canonicalPrices.length}개`);
    console.log(`  ⚠️ 비정상 modelId: ${nonCanonicalPrices.length}개`);
    
    if (result.prices.items.length > 0) {
      console.log('\n  📋 목록:');
      result.prices.items.forEach(item => {
        const status = item.isCanonical ? '✅' : '⚠️';
        const hasPrice = item.gradePrices && (item.gradePrices.A > 0 || item.gradePrices.B > 0);
        const priceInfo = hasPrice ? `💰 A:${item.gradePrices?.A || 0}` : '❌ 가격없음';
        console.log(`    ${status} ${item.brand} - ${item.id.substring(0, 50)}... [${priceInfo}]`);
      });
    }
    
    console.log('\n============================================');
    
    return result;
    
  } catch (error) {
    console.error('❌ 웨어러블 스캔 실패:', error);
    throw error;
  }
}

/**
 * 🔍 웨어러블 마이그레이션 미리보기 (변경 없음)
 */
export async function previewWearableMigration(): Promise<void> {
  console.log('🎧 웨어러블 마이그레이션 미리보기 (Dry Run)');
  console.log('============================================');
  console.log('⚠️ 이 작업은 데이터를 변경하지 않습니다.\n');
  
  const scan = await scanWearableData();
  
  const nonCanonicalCatalog = scan.catalog.items.filter(i => !i.isCanonical);
  const nonCanonicalPrices = scan.prices.items.filter(i => !i.isCanonical);
  
  console.log('\n🔄 마이그레이션 예정 작업:');
  
  if (nonCanonicalCatalog.length > 0) {
    console.log(`\n📦 device_catalog ${nonCanonicalCatalog.length}개 변환 예정:`);
    nonCanonicalCatalog.forEach(item => {
      console.log(`  ${item.id}`);
      console.log(`    → ${item.suggestedId}`);
    });
  } else {
    console.log('\n📦 device_catalog: 변환할 항목 없음 ✅');
  }
  
  if (nonCanonicalPrices.length > 0) {
    console.log(`\n💰 unified_prices ${nonCanonicalPrices.length}개 modelId 수정 예정:`);
    nonCanonicalPrices.forEach(item => {
      const { cleanId } = cleanWearableModelId(item.modelId);
      console.log(`  ${item.id}`);
      console.log(`    modelId: ${item.modelId} → ${cleanId}`);
    });
  } else {
    console.log('\n💰 unified_prices: 변환할 항목 없음 ✅');
  }
  
  console.log('\n============================================');
  console.log('📌 실제 마이그레이션을 실행하려면:');
  console.log('   await window.__migrateWearables()');
}

/**
 * 🔄 웨어러블 마이그레이션 실행
 */
export async function migrateWearables(): Promise<WearableMigrationResult> {
  console.log('🎧 웨어러블 마이그레이션 실행');
  console.log('============================================');
  
  const result: WearableMigrationResult = {
    success: false,
    catalog: { scanned: 0, canonical: 0, nonCanonical: 0, migrated: 0, errors: [] },
    prices: { scanned: 0, canonical: 0, nonCanonical: 0, migrated: 0, errors: [] }
  };
  
  try {
    // 1. 먼저 스캔
    const scan = await scanWearableData();
    
    result.catalog.scanned = scan.catalog.total;
    result.prices.scanned = scan.prices.total;
    
    const nonCanonicalCatalog = scan.catalog.items.filter(i => !i.isCanonical);
    const nonCanonicalPrices = scan.prices.items.filter(i => !i.isCanonical);
    
    result.catalog.canonical = scan.catalog.items.filter(i => i.isCanonical).length;
    result.catalog.nonCanonical = nonCanonicalCatalog.length;
    result.prices.canonical = scan.prices.items.filter(i => i.isCanonical).length;
    result.prices.nonCanonical = nonCanonicalPrices.length;
    
    // 마이그레이션할 항목이 없으면 종료
    if (nonCanonicalCatalog.length === 0 && nonCanonicalPrices.length === 0) {
      console.log('\n✅ 모든 웨어러블 ID가 이미 canonical 형식입니다.');
      result.success = true;
      return result;
    }
    
    // 2. device_catalog 마이그레이션
    if (nonCanonicalCatalog.length > 0) {
      console.log(`\n🔄 device_catalog ${nonCanonicalCatalog.length}개 마이그레이션 시작...`);
      
      // 기존 canonical 모델 맵 생성
      const existingCanonical = new Map<string, any>();
      scan.catalog.items.filter(i => i.isCanonical).forEach(item => {
        existingCanonical.set(item.id, item);
      });
      
      for (const item of nonCanonicalCatalog) {
        try {
          const newId = item.suggestedId!;
          
          // 기존 문서 읽기
          const oldDocRef = doc(db, 'device_catalog', item.id);
          const oldDoc = await getDoc(oldDocRef);
          
          if (!oldDoc.exists()) {
            result.catalog.errors.push(`${item.id}: 문서 없음`);
            continue;
          }
          
          const oldData = oldDoc.data();
          
          // 이미 동일한 canonical ID가 존재하는지 확인
          const existingDocRef = doc(db, 'device_catalog', newId);
          const existingDoc = await getDoc(existingDocRef);
          
          if (existingDoc.exists()) {
            // 기존 문서와 병합 (옵션 추가)
            const existingData = existingDoc.data();
            const mergedStorageOptions = Array.from(new Set([
              ...(existingData.storageOptions || []),
              ...(oldData.storageOptions || [])
            ]));
            
            await setDoc(existingDocRef, {
              ...existingData,
              storageOptions: mergedStorageOptions,
              updatedAt: Timestamp.now(),
              updatedBy: 'wearable-migration'
            }, { merge: true });
            
            console.log(`  🔗 병합: ${item.id} → ${newId}`);
          } else {
            // 새 문서 생성
            const newData = {
              ...oldData,
              id: newId,
              updatedAt: Timestamp.now(),
              updatedBy: 'wearable-migration'
            };
            
            await setDoc(existingDocRef, newData);
            console.log(`  ✅ 생성: ${newId}`);
          }
          
          // 기존 문서 삭제
          await deleteDoc(oldDocRef);
          console.log(`  🗑️ 삭제: ${item.id}`);
          
          result.catalog.migrated++;
          
        } catch (error) {
          const msg = `${item.id}: ${(error as Error).message}`;
          result.catalog.errors.push(msg);
          console.error(`  ❌ 실패: ${msg}`);
        }
      }
    }
    
    // 3. unified_prices 마이그레이션
    if (nonCanonicalPrices.length > 0) {
      console.log(`\n🔄 unified_prices ${nonCanonicalPrices.length}개 마이그레이션 시작...`);
      
      for (const item of nonCanonicalPrices) {
        try {
          const oldDocRef = doc(db, 'unified_prices', item.id);
          const oldDoc = await getDoc(oldDocRef);
          
          if (!oldDoc.exists()) {
            result.prices.errors.push(`${item.id}: 문서 없음`);
            continue;
          }
          
          const oldData = oldDoc.data();
          const { cleanId: newModelId } = cleanWearableModelId(item.modelId);
          
          // 새 priceId 생성
          const newPriceId = generatePriceId(
            newModelId,
            {
              storage: oldData.variant?.storage || '기본',
              connectivity: undefined // 웨어러블은 connectivity 없음
            },
            'KR'
          );
          
          // 기존 문서와 같은 ID면 modelId만 업데이트
          if (item.id === newPriceId) {
            await setDoc(oldDocRef, {
              ...oldData,
              modelId: newModelId,
              updatedAt: Timestamp.now(),
              updatedBy: 'wearable-migration'
            });
            console.log(`  ✅ 업데이트: ${item.id} (modelId: ${newModelId})`);
          } else {
            // 새 문서 생성
            const newDocRef = doc(db, 'unified_prices', newPriceId);
            const newData = {
              ...oldData,
              id: newPriceId,
              modelId: newModelId,
              updatedAt: Timestamp.now(),
              updatedBy: 'wearable-migration'
            };
            
            await setDoc(newDocRef, newData);
            console.log(`  ✅ 생성: ${newPriceId}`);
            
            // 기존 문서 삭제
            await deleteDoc(oldDocRef);
            console.log(`  🗑️ 삭제: ${item.id}`);
          }
          
          result.prices.migrated++;
          
        } catch (error) {
          const msg = `${item.id}: ${(error as Error).message}`;
          result.prices.errors.push(msg);
          console.error(`  ❌ 실패: ${msg}`);
        }
      }
    }
    
    // 4. 결과 출력
    console.log('\n============================================');
    console.log('📊 마이그레이션 결과:');
    console.log(`\n📦 device_catalog:`);
    console.log(`  스캔: ${result.catalog.scanned}개`);
    console.log(`  정상: ${result.catalog.canonical}개`);
    console.log(`  비정상: ${result.catalog.nonCanonical}개`);
    console.log(`  마이그레이션: ${result.catalog.migrated}개`);
    if (result.catalog.errors.length > 0) {
      console.log(`  에러: ${result.catalog.errors.length}개`);
    }
    
    console.log(`\n💰 unified_prices:`);
    console.log(`  스캔: ${result.prices.scanned}개`);
    console.log(`  정상: ${result.prices.canonical}개`);
    console.log(`  비정상: ${result.prices.nonCanonical}개`);
    console.log(`  마이그레이션: ${result.prices.migrated}개`);
    if (result.prices.errors.length > 0) {
      console.log(`  에러: ${result.prices.errors.length}개`);
    }
    
    result.success = result.catalog.errors.length === 0 && result.prices.errors.length === 0;
    
    if (result.success) {
      console.log('\n🎉 웨어러블 마이그레이션 완료!');
    } else {
      console.log('\n⚠️ 일부 에러가 발생했습니다.');
    }
    
    return result;
    
  } catch (error) {
    console.error('❌ 웨어러블 마이그레이션 실패:', error);
    throw error;
  }
}

/**
 * 🔍 레거시 웨어러블 가격 컬렉션 확인
 */
export async function scanLegacyWearablePrices(): Promise<void> {
  console.log('🎧 레거시 웨어러블 가격 컬렉션 스캔...');
  console.log('============================================');
  
  const legacyCollections = [
    'grade_prices_apple_wearable',
    'grade_prices_samsung_wearable'
  ];
  
  for (const collectionName of legacyCollections) {
    try {
      const snapshot = await getDocs(collection(db, collectionName));
      
      console.log(`\n📦 ${collectionName}: ${snapshot.size}개`);
      
      if (snapshot.size > 0) {
        snapshot.docs.slice(0, 10).forEach(docSnap => {
          const data = docSnap.data();
          const prices = data.gradePrices || data.prices || {};
          const hasPrice = (prices.A || 0) > 0 || (prices.B || 0) > 0;
          console.log(`  - ${docSnap.id} [${hasPrice ? '💰' : '❌'}]`);
        });
        
        if (snapshot.size > 10) {
          console.log(`  ... 외 ${snapshot.size - 10}개`);
        }
      }
    } catch (error) {
      console.log(`\n📦 ${collectionName}: 컬렉션 없음 또는 에러`);
    }
  }
  
  console.log('\n============================================');
}

/**
 * 🧹 웨어러블 가격 데이터 정리 (Phase 1)
 * 
 * 작업 내용:
 * 1. 스마트폰 데이터의 deviceType을 'smartphone'으로 수정
 * 2. priceId 하이픈 중복 정리 (-- → -)
 * 3. 중복 가격 문서 정리
 * 4. 웨어러블 가격의 modelId가 device_catalog와 매칭되도록 수정
 */
export async function cleanupWearableData(): Promise<void> {
  console.log('🎧 웨어러블 가격 데이터 정리 시작 (Phase 1)');
  console.log('============================================');
  
  try {
    const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
    const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
    
    // device_catalog의 웨어러블 모델 ID 수집
    const wearableModelIds = new Set<string>();
    catalogSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      if (data.deviceType === 'wearable') {
        wearableModelIds.add(docSnap.id);
      }
    });
    console.log(`📦 웨어러블 모델 ${wearableModelIds.size}개 확인됨`);
    
    let fixedDeviceType = 0;
    let fixedPriceId = 0;
    let fixedModelId = 0;
    let deletedDuplicates = 0;
    let deletedOrphans = 0;
    const errors: string[] = [];
    
    // 웨어러블 가격만 필터링
    const wearablePrices = pricesSnapshot.docs.filter(d => {
      const data = d.data();
      return data.deviceType === 'wearable';
    });
    
    console.log(`\n💰 웨어러블 가격 ${wearablePrices.length}개 처리 시작...`);
    
    // 1단계: 스마트폰 데이터의 deviceType 수정
    console.log('\n🔧 1단계: 잘못된 deviceType 수정');
    
    for (const docSnap of wearablePrices) {
      const data = docSnap.data();
      const idLower = docSnap.id.toLowerCase();
      const modelIdLower = (data.modelId || '').toLowerCase();
      
      // 스마트폰인데 wearable로 저장된 경우
      if (idLower.startsWith('smartphone-') || modelIdLower.startsWith('smartphone-')) {
        try {
          await setDoc(doc(db, 'unified_prices', docSnap.id), {
            ...data,
            deviceType: 'smartphone',
            updatedAt: Timestamp.now(),
            updatedBy: 'wearable-cleanup-phase1'
          });
          
          console.log(`  ✅ ${docSnap.id.substring(0, 50)}...`);
          console.log(`     deviceType: 'wearable' → 'smartphone'`);
          fixedDeviceType++;
        } catch (error) {
          errors.push(`deviceType 수정 실패 ${docSnap.id}: ${(error as Error).message}`);
        }
      }
    }
    
    // 2단계: priceId 하이픈 중복 정리
    console.log('\n🔧 2단계: priceId 하이픈 중복 정리 (-- → -)');
    
    // 재조회 (1단계에서 deviceType 변경된 것 제외)
    const updatedPricesSnapshot = await getDocs(collection(db, 'unified_prices'));
    const updatedWearablePrices = updatedPricesSnapshot.docs.filter(d => {
      const data = d.data();
      return data.deviceType === 'wearable' && d.id.includes('--');
    });
    
    for (const docSnap of updatedWearablePrices) {
      const data = docSnap.data();
      const correctId = docSnap.id.replace(/--/g, '-');
      
      // 올바른 ID의 문서가 이미 존재하는지 확인
      const correctDocSnap = await getDoc(doc(db, 'unified_prices', correctId));
      
      if (correctDocSnap.exists()) {
        // 올바른 문서가 이미 있으면 잘못된 문서 삭제
        try {
          await deleteDoc(doc(db, 'unified_prices', docSnap.id));
          console.log(`  🗑️ 중복 삭제: ${docSnap.id}`);
          deletedDuplicates++;
        } catch (error) {
          errors.push(`중복 삭제 실패 ${docSnap.id}: ${(error as Error).message}`);
        }
      } else {
        // 올바른 문서가 없으면 새로 생성하고 잘못된 문서 삭제
        try {
          await setDoc(doc(db, 'unified_prices', correctId), {
            ...data,
            id: correctId,
            updatedAt: Timestamp.now(),
            updatedBy: 'wearable-cleanup-phase1'
          });
          
          await deleteDoc(doc(db, 'unified_prices', docSnap.id));
          
          console.log(`  ✅ ${docSnap.id}`);
          console.log(`     → ${correctId}`);
          fixedPriceId++;
        } catch (error) {
          errors.push(`priceId 수정 실패 ${docSnap.id}: ${(error as Error).message}`);
        }
      }
    }
    
    // 3단계: 고아 가격 확인 및 처리 옵션 표시
    console.log('\n🔧 3단계: 고아 가격 확인');
    
    const finalPricesSnapshot = await getDocs(collection(db, 'unified_prices'));
    const orphanPrices: { id: string; modelId: string }[] = [];
    
    finalPricesSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      if (data.deviceType === 'wearable') {
        const modelId = data.modelId || '';
        if (modelId && !wearableModelIds.has(modelId)) {
          // 스마트폰 데이터가 아닌 진짜 웨어러블 고아만 카운트
          if (!modelId.startsWith('smartphone-')) {
            orphanPrices.push({ id: docSnap.id, modelId });
          }
        }
      }
    });
    
    if (orphanPrices.length > 0) {
      console.log(`  ⚠️ 고아 가격 ${orphanPrices.length}개 발견 (device_catalog에 매칭되는 모델 없음)`);
      orphanPrices.forEach(p => {
        console.log(`    - ${p.id}`);
        console.log(`      modelId: ${p.modelId}`);
      });
      console.log('  💡 이 가격들은 수동 확인이 필요합니다.');
    } else {
      console.log('  ✅ 고아 가격 없음');
    }
    
    // 결과 출력
    console.log('\n============================================');
    console.log('📊 Phase 1 정리 결과:');
    console.log(`  ✅ deviceType 수정: ${fixedDeviceType}개`);
    console.log(`  ✅ priceId 수정: ${fixedPriceId}개`);
    console.log(`  🗑️ 중복 삭제: ${deletedDuplicates}개`);
    console.log(`  ⚠️ 고아 가격 (확인 필요): ${orphanPrices.length}개`);
    
    if (errors.length > 0) {
      console.log(`\n❌ 에러: ${errors.length}개`);
      errors.forEach(e => console.log(`  - ${e}`));
    } else {
      console.log('\n🎉 Phase 1 완료!');
      console.log('\n📌 다음 단계:');
      console.log('   await window.__diagnoseWearablePrices()  // 결과 확인');
    }
    
  } catch (error) {
    console.error('❌ 데이터 정리 실패:', error);
  }
}

/**
 * 🔍 웨어러블 데이터 정리 미리보기 (Dry Run - Phase 1)
 */
export async function previewCleanup(): Promise<void> {
  console.log('🔍 웨어러블 데이터 정리 미리보기 (Phase 1 - Dry Run)');
  console.log('============================================');
  console.log('⚠️ 이 작업은 데이터를 변경하지 않습니다.\n');
  
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
  
  // device_catalog의 웨어러블 모델 ID 수집
  const wearableModelIds = new Set<string>();
  catalogSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'wearable') {
      wearableModelIds.add(docSnap.id);
    }
  });
  
  // 웨어러블 가격만 필터링
  const wearablePrices = pricesSnapshot.docs.filter(d => d.data().deviceType === 'wearable');
  
  console.log(`📦 웨어러블 모델: ${wearableModelIds.size}개`);
  console.log(`💰 웨어러블 가격: ${wearablePrices.length}개\n`);
  
  // 1. 잘못된 deviceType (스마트폰인데 wearable로 저장)
  console.log('🔧 1. 수정 예정: deviceType 오류 (스마트폰 → wearable 오염)');
  let wrongDeviceType = 0;
  
  wearablePrices.forEach(docSnap => {
    const data = docSnap.data();
    const idLower = docSnap.id.toLowerCase();
    const modelIdLower = (data.modelId || '').toLowerCase();
    
    if (idLower.startsWith('smartphone-') || modelIdLower.startsWith('smartphone-')) {
      console.log(`  - ${docSnap.id.substring(0, 60)}...`);
      console.log(`    현재: deviceType='wearable'`);
      console.log(`    수정: deviceType='smartphone'`);
      wrongDeviceType++;
    }
  });
  
  if (wrongDeviceType === 0) {
    console.log('  없음 ✅');
  }
  
  // 2. 하이픈 2개 문제
  console.log('\n🔧 2. 수정 예정: priceId 하이픈 중복 (--)');
  let doubleHyphen = 0;
  
  wearablePrices.forEach(docSnap => {
    if (docSnap.id.includes('--')) {
      const correctId = docSnap.id.replace(/--/g, '-');
      console.log(`  - ${docSnap.id}`);
      console.log(`    → ${correctId}`);
      doubleHyphen++;
    }
  });
  
  if (doubleHyphen === 0) {
    console.log('  없음 ✅');
  }
  
  // 3. 고아 가격 (device_catalog에 매칭 안 되는 가격)
  console.log('\n🔧 3. 확인 필요: 고아 가격');
  let orphanCount = 0;
  
  wearablePrices.forEach(docSnap => {
    const data = docSnap.data();
    const modelId = data.modelId || '';
    const idLower = docSnap.id.toLowerCase();
    
    // 스마트폰 데이터 제외
    if (idLower.startsWith('smartphone-') || modelId.startsWith('smartphone-')) {
      return;
    }
    
    if (modelId && !wearableModelIds.has(modelId)) {
      console.log(`  - ${docSnap.id}`);
      console.log(`    modelId: ${modelId} (device_catalog에 없음)`);
      orphanCount++;
    }
  });
  
  if (orphanCount === 0) {
    console.log('  없음 ✅');
  }
  
  // 4. 정상 데이터
  console.log('\n✅ 정상 웨어러블 가격:');
  let normalCount = 0;
  
  wearablePrices.forEach(docSnap => {
    const data = docSnap.data();
    const modelId = data.modelId || '';
    const idLower = docSnap.id.toLowerCase();
    
    // 스마트폰 데이터 제외
    if (idLower.startsWith('smartphone-') || modelId.startsWith('smartphone-')) {
      return;
    }
    
    // 하이픈 중복 제외
    if (docSnap.id.includes('--')) {
      return;
    }
    
    // 고아 제외
    if (modelId && !wearableModelIds.has(modelId)) {
      return;
    }
    
    const grades = data.gradePrices || {};
    console.log(`  ✅ ${docSnap.id}`);
    console.log(`     modelId: ${modelId}, A등급: ${grades.A || 0}원`);
    normalCount++;
  });
  
  console.log('\n============================================');
  console.log('📊 Phase 1 미리보기 요약:');
  console.log(`  🔄 deviceType 수정 예정: ${wrongDeviceType}개`);
  console.log(`  🔄 priceId 수정 예정: ${doubleHyphen}개`);
  console.log(`  ⚠️ 고아 가격 (확인 필요): ${orphanCount}개`);
  console.log(`  ✅ 정상 가격: ${normalCount}개`);
  console.log('\n📌 실제 정리를 실행하려면:');
  console.log('   await window.__cleanupWearableData()');
}

/**
 * 🔍 웨어러블 brand 필드 진단 (UI 필터링 문제 확인)
 */
export async function diagnoseBrandField(): Promise<void> {
  console.log('🔍 웨어러블 brand 필드 진단');
  console.log('============================================');
  
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  // 웨어러블만 필터링
  const wearablePrices = pricesSnapshot.docs.filter(d => {
    const data = d.data();
    return data.deviceType === 'wearable';
  });
  
  console.log(`\n💰 웨어러블 가격 ${wearablePrices.length}개의 brand 필드:`);
  
  const brandStats: Record<string, number> = {};
  
  wearablePrices.forEach(docSnap => {
    const data = docSnap.data();
    const brand = data.brand || '(undefined)';
    brandStats[brand] = (brandStats[brand] || 0) + 1;
    
    console.log(`  - ${docSnap.id}`);
    console.log(`    brand: "${brand}" (${typeof data.brand})`);
    console.log(`    deviceType: "${data.deviceType}"`);
  });
  
  console.log('\n📊 brand 분포:');
  Object.entries(brandStats).forEach(([brand, count]) => {
    console.log(`  ${brand}: ${count}개`);
  });
  
  console.log('\n⚠️ UI 필터링 조건:');
  console.log('   price.brand === "Apple" && price.deviceType === "wearable"');
  console.log('\n💡 brand가 "Apple"이 아니면 UI에서 필터링되어 제외됩니다!');
  
  // 수정 제안
  const needsFix = wearablePrices.filter(d => {
    const data = d.data();
    return data.brand !== 'Apple';
  });
  
  if (needsFix.length > 0) {
    console.log(`\n🔧 수정 필요: ${needsFix.length}개`);
    console.log('   await window.__fixWearableBrand() 실행으로 수정 가능');
  } else {
    console.log('\n✅ 모든 웨어러블 가격의 brand가 "Apple"입니다.');
  }
}

/**
 * 🔧 웨어러블 가격의 brand 필드 수정
 */
export async function fixWearableBrand(): Promise<void> {
  console.log('🔧 웨어러블 brand 필드 수정 시작...');
  console.log('============================================');
  
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  let fixed = 0;
  const errors: string[] = [];
  
  for (const docSnap of pricesSnapshot.docs) {
    const data = docSnap.data();
    
    // 웨어러블이면서 brand가 Apple이 아닌 경우
    if (data.deviceType === 'wearable' && data.brand !== 'Apple') {
      try {
        await setDoc(doc(db, 'unified_prices', docSnap.id), {
          ...data,
          brand: 'Apple',
          updatedAt: Timestamp.now(),
          updatedBy: 'brand-fix'
        });
        
        console.log(`  ✅ ${docSnap.id}`);
        console.log(`     brand: "${data.brand || '(undefined)'}" → "Apple"`);
        fixed++;
      } catch (error) {
        errors.push(`${docSnap.id}: ${(error as Error).message}`);
      }
    }
  }
  
  console.log('\n============================================');
  console.log(`📊 결과: ${fixed}개 수정됨`);
  
  if (errors.length > 0) {
    console.log(`❌ 에러: ${errors.length}개`);
    errors.forEach(e => console.log(`  - ${e}`));
  } else if (fixed > 0) {
    console.log('🎉 완료! 관리자 페이지를 새로고침하세요.');
  } else {
    console.log('ℹ️ 수정할 항목 없음');
  }
}

/**
 * 🔍 웨어러블 가격 상세 진단 (UI 표시 문제 디버깅)
 */
export async function diagnoseWearablePrices(): Promise<void> {
  console.log('🔍 웨어러블 가격 상세 진단 시작...');
  console.log('============================================');
  
  try {
    // 1. device_catalog에서 웨어러블 모델 조회
    console.log('\n📦 1단계: device_catalog 웨어러블 조회');
    const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
    
    const wearableModels: any[] = [];
    catalogSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      if (data.deviceType === 'wearable') {
        wearableModels.push({
          id: docSnap.id,
          modelKo: data.modelKo,
          brand: data.brand,
          deviceType: data.deviceType,
          storageOptions: data.storageOptions,
          connectivityOptions: data.connectivityOptions
        });
      }
    });
    
    console.log(`  웨어러블 모델: ${wearableModels.length}개`);
    wearableModels.forEach(m => {
      console.log(`    - ${m.modelKo}`);
      console.log(`      id: ${m.id}`);
      console.log(`      deviceType: ${m.deviceType}`);
      console.log(`      storageOptions: ${JSON.stringify(m.storageOptions)}`);
    });
    
    // 2. unified_prices에서 웨어러블 가격 조회
    console.log('\n💰 2단계: unified_prices 웨어러블 조회');
    const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
    
    const wearablePrices: any[] = [];
    pricesSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      if (data.deviceType === 'wearable') {
        wearablePrices.push({
          id: docSnap.id,
          modelId: data.modelId,
          brand: data.brand,
          deviceType: data.deviceType,
          variant: data.variant,
          gradePrices: data.gradePrices
        });
      }
    });
    
    console.log(`  웨어러블 가격: ${wearablePrices.length}개`);
    wearablePrices.forEach(p => {
      console.log(`    - ${p.id}`);
      console.log(`      modelId: ${p.modelId}`);
      console.log(`      deviceType: ${p.deviceType}`);
      console.log(`      variant: ${JSON.stringify(p.variant)}`);
      console.log(`      gradePrices: ${JSON.stringify(p.gradePrices)}`);
    });
    
    // 3. 매칭 검증
    console.log('\n🔗 3단계: device_catalog ↔ unified_prices 매칭 검증');
    
    wearableModels.forEach(model => {
      console.log(`\n  📱 ${model.modelKo} (${model.id})`);
      
      // 이 모델에 해당하는 가격 찾기
      const matchingPrices = wearablePrices.filter(p => p.modelId === model.id);
      
      if (matchingPrices.length > 0) {
        console.log(`    ✅ 매칭된 가격: ${matchingPrices.length}개`);
        matchingPrices.forEach(p => {
          console.log(`      - ${p.id} (A: ${p.gradePrices?.A || 0}원)`);
        });
      } else {
        console.log(`    ❌ 매칭된 가격 없음!`);
        
        // 유사한 가격 찾기
        const similarPrices = wearablePrices.filter(p => 
          p.id.includes(model.id.split('-').pop() || '') ||
          p.modelId?.includes(model.id.split('-').pop() || '')
        );
        
        if (similarPrices.length > 0) {
          console.log(`    💡 유사한 가격 발견:`);
          similarPrices.forEach(p => {
            console.log(`      - ${p.id} (modelId: ${p.modelId})`);
          });
        }
      }
    });
    
    // 4. 고아 가격 (device_catalog에 없는 가격)
    console.log('\n🔍 4단계: 고아 가격 검사 (device_catalog에 없는 modelId)');
    const modelIds = new Set(wearableModels.map(m => m.id));
    
    const orphanPrices = wearablePrices.filter(p => !modelIds.has(p.modelId));
    if (orphanPrices.length > 0) {
      console.log(`  ⚠️ 고아 가격: ${orphanPrices.length}개`);
      orphanPrices.forEach(p => {
        console.log(`    - ${p.id}`);
        console.log(`      modelId: ${p.modelId} (device_catalog에 없음)`);
      });
    } else {
      console.log('  ✅ 고아 가격 없음');
    }
    
    // 5. generatePriceId 테스트
    console.log('\n🔧 5단계: generatePriceId 형식 검증');
    wearableModels.forEach(model => {
      const expectedPriceId = generatePriceId(model.id, { storage: '기본' }, 'KR');
      const actualPrice = wearablePrices.find(p => p.modelId === model.id);
      
      console.log(`  ${model.modelKo}:`);
      console.log(`    예상 priceId: ${expectedPriceId}`);
      console.log(`    실제 priceId: ${actualPrice?.id || '없음'}`);
      
      if (actualPrice && actualPrice.id !== expectedPriceId) {
        console.log(`    ⚠️ priceId 불일치!`);
      }
    });
    
    console.log('\n============================================');
    
  } catch (error) {
    console.error('❌ 진단 실패:', error);
  }
}

// 🎯 브라우저 콘솔에서 실행할 수 있도록 window에 등록
/**
 * 🔍 전체 데이터 건강 상태 진단 (모든 카테고리)
 * 태블릿, 스마트폰, 웨어러블 전체 데이터의 ID 일관성 검사
 */
export async function diagnoseAllData(): Promise<void> {
  console.log('🔍 전체 데이터 건강 상태 진단');
  console.log('============================================');
  
  const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  // 카테고리별 분류
  const stats = {
    tablet: { catalog: 0, prices: 0, catalogIssues: 0, priceIssues: 0, orphanPrices: 0 },
    smartphone: { catalog: 0, prices: 0, catalogIssues: 0, priceIssues: 0, orphanPrices: 0 },
    wearable: { catalog: 0, prices: 0, catalogIssues: 0, priceIssues: 0, orphanPrices: 0 },
    unknown: { catalog: 0, prices: 0, catalogIssues: 0, priceIssues: 0, orphanPrices: 0 }
  };
  
  const catalogIds = new Set<string>();
  const issues: { category: string; type: string; id: string; issue: string }[] = [];
  
  // 1. device_catalog 분석
  console.log('\n📦 1단계: device_catalog 분석...');
  
  catalogSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    const deviceType = data.deviceType || 'unknown';
    const category = deviceType as keyof typeof stats;
    
    if (stats[category]) {
      stats[category].catalog++;
    } else {
      stats.unknown.catalog++;
    }
    
    catalogIds.add(docSnap.id);
    
    // ID 문제 검사
    const id = docSnap.id;
    let hasIssue = false;
    let issueDesc = '';
    
    // storage가 ID에 포함된 경우
    if (id.match(/-\d+(gb|tb)/i)) {
      hasIssue = true;
      issueDesc = 'storage 포함';
    }
    // connectivity가 ID에 포함된 경우 (wearable 제외)
    else if (deviceType !== 'wearable' && id.match(/-(wifi|cellular|lte|5g)$/i)) {
      hasIssue = true;
      issueDesc = 'connectivity 포함';
    }
    // 3자리 이상 순번이 포함된 경우
    else if (id.match(/-\d{3,}$/)) {
      hasIssue = true;
      issueDesc = '긴 순번 포함';
    }
    // deviceType과 ID 접두사 불일치
    else if (deviceType === 'tablet' && !id.includes('tablet')) {
      hasIssue = true;
      issueDesc = 'tablet인데 ID에 tablet 없음';
    }
    else if (deviceType === 'smartphone' && !id.includes('smartphone')) {
      hasIssue = true;
      issueDesc = 'smartphone인데 ID에 smartphone 없음';
    }
    else if (deviceType === 'wearable' && !id.includes('wearable')) {
      hasIssue = true;
      issueDesc = 'wearable인데 ID에 wearable 없음';
    }
    
    if (hasIssue) {
      if (stats[category]) {
        stats[category].catalogIssues++;
      }
      issues.push({ category: deviceType, type: 'catalog', id, issue: issueDesc });
    }
  });
  
  // 2. unified_prices 분석
  console.log('💰 2단계: unified_prices 분석...');
  
  pricesSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    const deviceType = data.deviceType || 'unknown';
    const modelId = data.modelId || '';
    const category = deviceType as keyof typeof stats;
    
    if (stats[category]) {
      stats[category].prices++;
    } else {
      stats.unknown.prices++;
    }
    
    let hasIssue = false;
    let issueDesc = '';
    
    // modelId가 device_catalog에 없는 경우 (고아 가격)
    if (modelId && !catalogIds.has(modelId)) {
      hasIssue = true;
      issueDesc = 'modelId가 device_catalog에 없음 (고아)';
      if (stats[category]) {
        stats[category].orphanPrices++;
      }
    }
    
    // priceId에 하이픈 2개가 있는 경우
    if (docSnap.id.includes('--')) {
      hasIssue = true;
      issueDesc = (issueDesc ? issueDesc + ', ' : '') + 'priceId에 하이픈 중복';
    }
    
    // deviceType과 modelId 불일치
    if (deviceType === 'wearable' && modelId.startsWith('smartphone-')) {
      hasIssue = true;
      issueDesc = (issueDesc ? issueDesc + ', ' : '') + 'deviceType/modelId 불일치';
    }
    
    if (hasIssue) {
      if (stats[category]) {
        stats[category].priceIssues++;
      }
      issues.push({ category: deviceType, type: 'price', id: docSnap.id, issue: issueDesc });
    }
  });
  
  // 결과 출력
  console.log('\n============================================');
  console.log('📊 전체 데이터 건강 상태 보고서');
  console.log('============================================\n');
  
  // 요약 테이블
  console.log('┌─────────────┬──────────┬──────────┬──────────┬──────────┬──────────┐');
  console.log('│ 카테고리    │ 모델(개) │ 가격(개) │ 모델문제 │ 가격문제 │ 고아가격 │');
  console.log('├─────────────┼──────────┼──────────┼──────────┼──────────┼──────────┤');
  
  Object.entries(stats).forEach(([category, s]) => {
    if (s.catalog > 0 || s.prices > 0) {
      const catName = category.padEnd(11);
      const catalogStr = String(s.catalog).padStart(8);
      const pricesStr = String(s.prices).padStart(8);
      const catIssueStr = String(s.catalogIssues).padStart(8);
      const priceIssueStr = String(s.priceIssues).padStart(8);
      const orphanStr = String(s.orphanPrices).padStart(8);
      console.log(`│ ${catName} │${catalogStr} │${pricesStr} │${catIssueStr} │${priceIssueStr} │${orphanStr} │`);
    }
  });
  
  console.log('└─────────────┴──────────┴──────────┴──────────┴──────────┴──────────┘');
  
  // 총계
  const totalCatalog = Object.values(stats).reduce((sum, s) => sum + s.catalog, 0);
  const totalPrices = Object.values(stats).reduce((sum, s) => sum + s.prices, 0);
  const totalCatalogIssues = Object.values(stats).reduce((sum, s) => sum + s.catalogIssues, 0);
  const totalPriceIssues = Object.values(stats).reduce((sum, s) => sum + s.priceIssues, 0);
  const totalOrphanPrices = Object.values(stats).reduce((sum, s) => sum + s.orphanPrices, 0);
  
  console.log(`\n📈 총계:`);
  console.log(`  모델: ${totalCatalog}개 (문제: ${totalCatalogIssues}개, ${((totalCatalogIssues/totalCatalog)*100).toFixed(1)}%)`);
  console.log(`  가격: ${totalPrices}개 (문제: ${totalPriceIssues}개, ${((totalPriceIssues/totalPrices)*100).toFixed(1)}%)`);
  console.log(`  고아 가격: ${totalOrphanPrices}개`);
  
  // 건강 상태 판정
  const healthScore = 100 - ((totalCatalogIssues + totalPriceIssues) / (totalCatalog + totalPrices) * 100);
  console.log(`\n🏥 데이터 건강 점수: ${healthScore.toFixed(1)}%`);
  
  if (healthScore >= 95) {
    console.log('  ✅ 양호 - 대부분의 데이터가 정상입니다.');
  } else if (healthScore >= 80) {
    console.log('  ⚠️ 주의 - 일부 데이터 정리가 필요합니다.');
  } else if (healthScore >= 60) {
    console.log('  🔶 경고 - 상당수의 데이터에 문제가 있습니다.');
  } else {
    console.log('  🔴 위험 - 데이터 마이그레이션이 시급합니다.');
  }
  
  // 문제 상세 (최대 20개)
  if (issues.length > 0) {
    console.log(`\n⚠️ 문제 목록 (${Math.min(issues.length, 20)}/${issues.length}개 표시):`);
    issues.slice(0, 20).forEach(issue => {
      console.log(`  [${issue.category}/${issue.type}] ${issue.id}`);
      console.log(`    └ ${issue.issue}`);
    });
    
    if (issues.length > 20) {
      console.log(`  ... 그 외 ${issues.length - 20}개 더 있음`);
    }
  }
  
  console.log('\n============================================');
  console.log('📌 데이터 정리가 필요하면:');
  console.log('   await window.__previewCleanup()  // 미리보기');
  console.log('   await window.__cleanupWearableData()  // 실행');
  
  return;
}

if (typeof window !== 'undefined') {
  (window as any).__scanWearableData = scanWearableData;
  (window as any).__previewWearableMigration = previewWearableMigration;
  (window as any).__migrateWearables = migrateWearables;
  (window as any).__scanLegacyWearablePrices = scanLegacyWearablePrices;
  (window as any).__diagnoseWearablePrices = diagnoseWearablePrices;
  (window as any).__previewCleanup = previewCleanup;
  (window as any).__cleanupWearableData = cleanupWearableData;
  (window as any).__diagnoseAllData = diagnoseAllData;
  (window as any).__diagnoseBrandField = diagnoseBrandField;
  (window as any).__fixWearableBrand = fixWearableBrand;
  
  console.log('🎧 데이터 관리 함수 등록 완료:');
  console.log('  🔍 진단:');
  console.log('    - window.__diagnoseAllData(): 전체 데이터 건강 상태');
  console.log('    - window.__diagnoseWearablePrices(): 웨어러블 가격 진단');
  console.log('    - window.__diagnoseBrandField(): brand 필드 진단');
  console.log('    - window.__scanWearableData(): 웨어러블 데이터 스캔');
  console.log('  🔧 정리:');
  console.log('    - window.__previewCleanup(): 데이터 정리 미리보기');
  console.log('    - window.__cleanupWearableData(): 오염 데이터 정리');
  console.log('    - window.__fixWearableBrand(): brand 필드 수정');
  console.log('  🔄 마이그레이션:');
  console.log('    - window.__previewWearableMigration(): 마이그레이션 미리보기');
  console.log('    - window.__migrateWearables(): 실제 마이그레이션 실행');
}

